/**
 * Phase 8 T-06b §20.C · Scope pill at bottom-left of composer.
 *
 * Dropdown: `Org database` (default) · `Career page submissions` ·
 * `External (paid sources)` · `+ Create new source`. The current selection
 * is persisted to `localStorage` so users keep their preferred source across
 * sessions within a single scope.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';

export const SCOPE_OPTIONS = [
  { id: 'org', label: 'Org database' },
  { id: 'career', label: 'Career page submissions' },
  { id: 'external', label: 'External (paid sources)' },
] as const;

export type ScopeId = (typeof SCOPE_OPTIONS)[number]['id'];

interface Props {
  value: ScopeId;
  onChange: (next: ScopeId) => void;
  onCreateNew?: () => void;
}

export function AgentScopePill({ value, onChange, onCreateNew }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = SCOPE_OPTIONS.find((o) => o.id === value) ?? SCOPE_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-7 items-center gap-1.5 rounded-[10px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: 'var(--ggj-accent,#10B981)' }}
          aria-hidden
        />
        {selected.label}
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-30 mb-1 w-[240px] rounded-[10px] border py-1 shadow-lg"
          style={{
            background: 'var(--vscode-bg-elevated)',
            borderColor: 'var(--vscode-border-primary)',
          }}
        >
          {SCOPE_OPTIONS.map((opt) => {
            const active = opt.id === value;
            return (
              <button
                key={opt.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                <span>{opt.label}</span>
                {active ? (
                  <Check
                    className="h-3.5 w-3.5"
                    style={{ color: 'var(--ggj-accent,#10B981)' }}
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
          <div
            className="my-1 h-px"
            style={{ background: 'var(--vscode-border-primary)' }}
            aria-hidden
          />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onCreateNew?.();
            }}
            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
            <Plus className="h-3 w-3" aria-hidden />
            Create new
          </button>
        </div>
      ) : null}
    </div>
  );
}
